import React, { useState } from 'react';
import { Alert, Button, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { useDispatch } from 'react-redux';
import { PRINTFULL_ID, DRAWPILLOWS_ID, TEST_ID } from '../../constants';
import { createShipment } from '../../api/shipments';
import { Notifications } from '../../store/slices';
import { AppDialog } from '../../components/ui';
import { useStyles } from './styles';

interface CreateShipmentPopupProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const CLIENTS = [
    { id: PRINTFULL_ID, name: 'Printful' },
    { id: DRAWPILLOWS_ID, name: 'DrawPillows' },
    { id: '10567', name: 'Printify' },
    { id: TEST_ID, name: 'Test' },
    { id: '98567', name: 'Printify Test' },
];

const nowForInput = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
};

/** Registers a manual shipment (tracking number + cost) for one or more orders of a client. */
const CreateShipmentPopup: React.FC<CreateShipmentPopupProps> = ({ isOpen, onClose, onSuccess }) => {
    const classes = useStyles();
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch();

    const [clientId, setClientId] = useState<string>('');
    const [orderIds, setOrderIds] = useState<string>('');
    const [trackingNumber, setTrackingNumber] = useState<string>('');
    const [createDate, setCreateDate] = useState<string>(nowForInput);
    const [shipmentCost, setShipmentCost] = useState<string>('');
    const [backendErrors, setBackendErrors] = useState<string[]>([]);

    const parsedOrderIds = orderIds.split(',').map(id => parseInt(id.trim(), 10)).filter(id => !isNaN(id));
    const isOrderIdsValid = orderIds.length === 0 || parsedOrderIds.length > 0;

    const isFormValid = clientId.trim() !== '' &&
        parsedOrderIds.length > 0 &&
        trackingNumber.trim() !== '' &&
        createDate !== '' &&
        shipmentCost !== '' && !isNaN(parseFloat(shipmentCost));

    const handleSubmit = async () => {
        if (loading) return;
        if (!isFormValid) {
            dispatch(Notifications.setNotifications({ text: 'Please fill in all required fields correctly.', type: 'warning' }));
            return;
        }

        const payload = {
            clientId: parseInt(clientId, 10),
            orderIdArr: parsedOrderIds,
            trackingNumber: trackingNumber.trim(),
            createDate: new Date(createDate).toISOString(),
            shipmentCost: parseFloat(shipmentCost)
        };

        setBackendErrors([]);
        setLoading(true);
        try {
            await createShipment(payload);
            dispatch(Notifications.setNotifications({ text: 'Shipment created', type: 'success' }));
            onSuccess();
            handleClose();
        } catch (error: any) {
            console.error('Failed to create shipment', error);
            if (error.errors && Array.isArray(error.errors)) {
                setBackendErrors(error.errors);
            }
            dispatch(Notifications.setNotifications({ text: error.message || 'Failed to create shipment', type: 'error' }));
        } finally {
            setLoading(false);
        }
    };

    const handleCostChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        // Allow empty string or numbers with up to 2 decimal places
        if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
            setShipmentCost(value);
        }
    };

    const handleClose = () => {
        setClientId('');
        setOrderIds('');
        setTrackingNumber('');
        setCreateDate(nowForInput());
        setShipmentCost('');
        setBackendErrors([]);
        onClose();
    };

    return (
        <AppDialog
            open={isOpen}
            onClose={handleClose}
            title="Create shipment"
            description="Registers a shipment that was made outside the integration. The cost is attached to the first order id only."
            pending={loading}
            maxWidth="sm"
            actions={(
                <>
                    <Button variant="outlined" onClick={handleClose} disabled={loading}>Cancel</Button>
                    <Button variant="contained" onClick={handleSubmit} disabled={loading || !isFormValid}>Create shipment</Button>
                </>
            )}
        >
            {backendErrors.length > 0 && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    <ul className={classes.errors}>
                        {backendErrors.map((err, i) => (
                            <li key={i}>{err}</li>
                        ))}
                    </ul>
                </Alert>
            )}
            <div style={{ display: 'grid', gap: 14, paddingTop: 4 }}>
                <FormControl fullWidth required size="small">
                    <InputLabel id="client-select-label">Client</InputLabel>
                    <Select
                        labelId="client-select-label"
                        id="client-select"
                        value={clientId}
                        label="Client"
                        onChange={(e) => setClientId(e.target.value as string)}
                    >
                        {CLIENTS.map((client) => (
                            <MenuItem key={client.id} value={client.id}>
                                {client.name} ({client.id})
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    label="Order IDs"
                    size="small"
                    fullWidth
                    required
                    placeholder="12001, 12002"
                    value={orderIds}
                    onChange={(e) => setOrderIds(e.target.value)}
                    error={!isOrderIdsValid}
                    helperText={!isOrderIdsValid ? 'Enter at least one valid order id' : 'One or more order ids, separated by commas'}
                    inputProps={{ inputMode: 'numeric' }}
                />
                <TextField
                    label="Tracking number"
                    size="small"
                    fullWidth
                    required
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                />
                <TextField
                    label="Shipped at"
                    type="datetime-local"
                    size="small"
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                    value={createDate}
                    onChange={(e) => setCreateDate(e.target.value)}
                />
                <TextField
                    label="Shipment cost"
                    type="number"
                    size="small"
                    inputProps={{ step: '0.01', min: '0', inputMode: 'decimal' }}
                    fullWidth
                    required
                    value={shipmentCost}
                    onChange={handleCostChange}
                    helperText="Added to the first order id only"
                />
            </div>
        </AppDialog>
    );
};

export default CreateShipmentPopup;
